export class BackgroundMusicComponent extends HTMLElement {
    private audioElement!: HTMLAudioElement;
    private popupElement!: HTMLDivElement;
    private volumeSlider!: HTMLInputElement;
    private muteButton!: HTMLButtonElement;
    private soundIcon!: HTMLButtonElement;

    // Store event handlers as bound methods to properly remove them later
    private readonly handleSoundIconClick: () => void;
    private readonly handleClosePopup: () => void;
    private readonly handleVolumeChange: () => void;
    private readonly handleMuteToggle: () => void;

    public constructor() {
        super();
        this.attachShadow({ mode: "open" });

        // Bind methods to this instance
        this.handleSoundIconClick = this.openPopupAndPlayMusic.bind(this);
        this.handleClosePopup = this.closePopup.bind(this);
        this.handleVolumeChange = this.updateVolume.bind(this);
        this.handleMuteToggle = this.toggleMute.bind(this);
    }

    public connectedCallback(): void {
        this.render();
        this.setupEventListeners();

        // Add a global click event listener to enable audio after any interaction
        document.addEventListener("click", this.enableAudio.bind(this), { once: true });
    }

    public disconnectedCallback(): void {
        this.removeEventListeners();
    }

    private render(): void {
        this.shadowRoot!.innerHTML = `
            <style>
                .music-popup {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100vh;
                    background: rgba(0, 0, 0, 0.9);
                    color: white;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    text-align: center;
                    z-index: 10000;
                    display: none;
                }
                .popup-title {
                    font-size: 32px;
                    font-weight: bold;
                    margin-bottom: 20px;
                }
                .popup-content {
                    background: rgba(255, 255, 255, 0.1);
                    padding: 20px;
                    border-radius: 10px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }
                .music-popup input {
                    width: 80%;
                    margin-top: 10px;
                }
                .button {
                    margin-top: 20px;
                    padding: 10px 20px;
                    background: red;
                    color: white;
                    border: none;
                    cursor: pointer;
                    border-radius: 5px;
                    font-size: 18px;
                }
                #mute-button {
                    background: gray;
                    font-size: 18px;
                    margin-top: 10px;
                }
                button#sound-icon {
                    position: fixed;
                    top: 10px;
                    right: 20px;
                    font-size: 24px;
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: white;
                }
            </style>
            <button id="sound-icon">🔊</button>
            <div id="music-popup" class="music-popup">
                <div class="popup-title">Audio Settings</div>
                <div class="popup-content">
                    <label>Volume</label>
                    <input type="range" id="volume-slider" min="0" max="1" step="0.01">
                    <button id="mute-button" class="button">Mute</button>
                    <button id="close-popup" class="button">Close</button>
                </div>
            </div>
            <audio id="bg-music" preload="auto">
                <source src="/assets/music/background.mp3" type="audio/mp3">
            </audio>
        `;

        this.audioElement = this.shadowRoot!.querySelector("#bg-music")!;
        this.popupElement = this.shadowRoot!.querySelector("#music-popup")!;
        this.volumeSlider = this.shadowRoot!.querySelector("#volume-slider")!;
        this.muteButton = this.shadowRoot!.querySelector("#mute-button")!;
        this.soundIcon = this.shadowRoot!.querySelector("#sound-icon")!;

        // Load saved settings
        const savedVolume: string | null = localStorage.getItem("bg-music-volume");
        const savedMuted: string | null = localStorage.getItem("bg-music-muted");

        this.audioElement.volume = savedVolume ? parseFloat(savedVolume) : 0.5;
        this.audioElement.muted = savedMuted === "true";
        this.volumeSlider.value = this.audioElement.volume.toString();
        this.updateMuteButtonText();
    }

    private setupEventListeners(): void {
        this.soundIcon.addEventListener("click", this.handleSoundIconClick);
        this.shadowRoot!.querySelector("#close-popup")!.addEventListener("click", this.handleClosePopup);
        this.volumeSlider.addEventListener("input", this.handleVolumeChange);
        this.muteButton.addEventListener("click", this.handleMuteToggle);
    }

    private removeEventListeners(): void {
        this.soundIcon.removeEventListener("click", this.handleSoundIconClick);
        this.shadowRoot!.querySelector("#close-popup")!.removeEventListener("click", this.handleClosePopup);
        this.volumeSlider.removeEventListener("input", this.handleVolumeChange);
        this.muteButton.removeEventListener("click", this.handleMuteToggle);
    }

    private openPopupAndPlayMusic(): void {
        this.popupElement.style.display = "flex";
        this.playMusic();
    }

    private closePopup(): void {
        this.popupElement.style.display = "none";
    }

    private updateVolume(): void {
        this.audioElement.volume = parseFloat(this.volumeSlider.value);
        localStorage.setItem("bg-music-volume", this.audioElement.volume.toString());
    }

    private toggleMute(): void {
        this.audioElement.muted = !this.audioElement.muted;
        localStorage.setItem("bg-music-muted", this.audioElement.muted.toString());
        this.updateMuteButtonText();
    }

    private updateMuteButtonText(): void {
        this.muteButton.textContent = this.audioElement.muted ? "Unmute" : "Mute";
    }

    private async enableAudio(): Promise<void> {
        // This unlocks audio playback on mobile devices
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
        const context: AudioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        await context.resume().then(() => {
            console.log("Audio context started");
        });
    }

    private playMusic(): void {
        // Explicit user interaction has occurred, so attempt to play
        if (this.audioElement.paused) {
            this.audioElement.play().catch((error: unknown) => {
                console.error("Error playing audio:", error);

                // If playback fails, inform the user
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                if (this.popupElement) {
                    const errorMsg: HTMLParagraphElement = document.createElement("p");
                    errorMsg.textContent = "Audio playback failed. Please try clicking the sound icon again.";
                    errorMsg.style.color = "red";
                    this.shadowRoot?.querySelector(".popup-content")?.appendChild(errorMsg);

                    // Auto-remove after 3 seconds
                    setTimeout(() => {
                        errorMsg.remove();
                    }, 3000);
                }
            });
        }
    }
}

customElements.define("background-music", BackgroundMusicComponent);
document.body.appendChild(document.createElement("background-music"));
